import { useMemo, useRef, useState } from 'react';
import { FormControl, MenuItem, Select } from '@mui/material';
import atlasEmptyState from 'src/components/CategoryConfiguration/assets/atlas-empty-state.svg';
import type { SelectChangeEvent } from '@mui/material';
import { Button, Spinner } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { DropdownSelect } from 'src/components/molecules/DropdownSelect/DropdownSelect';
import { useCloseModal } from 'src/hooks/useCloseModal';
import {
  LANGUAGE_OPTIONS,
  LanguageOption,
  sortLanguageOptionsByLabel,
} from 'src/utils/languages';
import { FormCreationMode } from '../../types';
import {
  InitialStateContainer,
  InitialStateDescription,
  InitialStateIllustration,
  InitialStateTextContainer,
  InitialStateTitle,
  LanguageDropdownContainer,
  LanguageDropdownWrapper,
  LanguagesControlRow,
  LanguageVariantLabel,
  LanguageVariantsContainer,
  LanguageVariantWrapper,
  LoadingStateContainer,
  SectionContainer,
} from './LanguageVariants.styles';

interface Props {
  mode: FormCreationMode;
  selectedLanguages: LanguageOption[];
  onAddDefaultLanguage: () => void;
  editingLocale?: string;
  onEditingLocaleChange?: (locale: string) => void;
}

export const LanguageVariants = ({
  mode,
  selectedLanguages,
  onAddDefaultLanguage,
  editingLocale,
  onEditingLocaleChange,
}: Props) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<LanguageOption | null>(
    null,
  );

  const isEditingLocaleControlled =
    editingLocale !== undefined && onEditingLocaleChange !== undefined;

  const sortedLanguages = useMemo(
    () => sortLanguageOptionsByLabel(selectedLanguages),
    [selectedLanguages],
  );

  const selectedVariantLanguage = isEditingLocaleControlled
    ? (selectedLanguages.find((l) => l.value === editingLocale) ??
      selectedLanguages[0] ??
      null)
    : currentLanguage || selectedLanguages[0] || null;

  const defaultLanguageDropdownRef = useRef<HTMLDivElement>(null);

  useCloseModal(
    defaultLanguageDropdownRef,
    () => setShowDropdown(false),
    showDropdown,
  );

  const handleAddLanguageClick = () => {
    setShowDropdown(true);
  };

  const handleDefaultLanguageSelect = () => {
    onAddDefaultLanguage();
    setShowDropdown(false);
  };

  const handleLanguageVariantChange = (language: LanguageOption) => {
    if (isEditingLocaleControlled) {
      onEditingLocaleChange(language.value);
      return;
    }
    setCurrentLanguage(language);
  };

  if (mode === 'initial') {
    return (
      <SectionContainer $isInitialState data-testid="language-variants-section">
        <InitialStateContainer data-testid="language-variants-initial-state">
          <InitialStateIllustration
            src={atlasEmptyState}
            alt=""
            width={160}
            height={132}
            data-testid="language-variants-initial-illustration"
          />
          <InitialStateTextContainer>
            <InitialStateTitle data-testid="language-variants-initial-title">
              Choose a default language for this form
            </InitialStateTitle>
            <InitialStateDescription data-testid="language-variants-initial-description">
              Set the default language before you add content. Any translations
              or additional language variants will be based on this language.
            </InitialStateDescription>
          </InitialStateTextContainer>
          <Button
            variant="primary"
            size="medium"
            onClick={handleAddLanguageClick}
            data-testid="add-default-language-button"
          >
            <StyledIconInButton
              size="medium"
              position="left"
              className="uil uil-plus"
            />
            Select default language
          </Button>
          {showDropdown && (
            <LanguageDropdownWrapper
              ref={defaultLanguageDropdownRef}
              data-testid="language-dropdown-wrapper"
            >
              <DropdownSelect
                options={LANGUAGE_OPTIONS}
                value={null}
                onChange={() => handleDefaultLanguageSelect()}
                placeholder="Search language..."
                isSearchable
                manualMenuOpen
                defaultMenuIsOpen
                data-testid="language-dropdown-select"
              />
            </LanguageDropdownWrapper>
          )}
        </InitialStateContainer>
      </SectionContainer>
    );
  }

  if (mode === 'loading') {
    return (
      <SectionContainer data-testid="language-variants-section">
        <LoadingStateContainer data-testid="language-variants-loading-state">
          <Spinner variant="secondary" size="xlarge" />
        </LoadingStateContainer>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer data-testid="language-variants-section">
      <LanguageVariantsContainer data-testid="language-variants-container">
        <LanguageVariantWrapper>
          <LanguageVariantLabel data-testid="language-variants-label">
            Languages
          </LanguageVariantLabel>
          <LanguagesControlRow>
            <LanguageDropdownContainer>
              <FormControl fullWidth>
                <Select
                  value={selectedVariantLanguage?.value ?? ''}
                  onChange={(e: SelectChangeEvent<string>) => {
                    const lang = sortedLanguages.find(
                      (l) => l.value === e.target.value,
                    );
                    if (lang) handleLanguageVariantChange(lang);
                  }}
                  displayEmpty
                  data-testid="language-variants-dropdown"
                  inputProps={{ 'aria-label': 'Select language' }}
                >
                  {sortedLanguages.map((lang) => (
                    <MenuItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </LanguageDropdownContainer>
          </LanguagesControlRow>
        </LanguageVariantWrapper>
      </LanguageVariantsContainer>
    </SectionContainer>
  );
};
